import createDOMPurify from 'dompurify';
import escapeHTML from 'escape-html';
import katex from 'katex';
import showdown from 'showdown';
var DOMPurify = createDOMPurify(window);

// Ensure that any links generated either by Showdown or in the markdown/HTML
// passed to Showdown open in an external window.
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
  }
});
function targetBlank() {
  function filter(text) {
    return text.replace(/<a href=/g, '<a target="_blank" href=');
  }
  return [{
    type: 'output',
    filter: filter
  }];
}
var converter;
function renderMarkdown(markdown) {
  if (!converter) {
    // see https://github.com/showdownjs/showdown#valid-options
    converter = new showdown.Converter({
      extensions: [targetBlank],
      simplifiedAutoLink: true,
      excludeTrailingPunctuationFromURLs: true,
      // Since we're using simplifiedAutoLink we also use
      // literalMidWordUnderscores because otherwise _'s in URLs get
      // transformed into <em>'s.
      // See https://github.com/showdownjs/showdown/issues/211
      literalMidWordUnderscores: true,
      // Enable strikethrough, which is disabled by default
      strikethrough: true
    });
  }
  return converter.makeHtml(markdown);
}
function mathPlaceholder(id) {
  return '{math:' + id.toString() + '}';
}
/**
 * Parses a string containing mixed markdown and LaTeX in between
 * '$$..$$' or '\( ... \)' delimiters and returns an object containing a
 * list of math blocks found in the string, plus the input string with math
 * blocks replaced by placeholders.
 */
function extractMath(content) {
  var mathBlocks = [];
  var pos = 0;
  var replacedContent = content;
  while (true) {
    var blockMathStart = replacedContent.indexOf('$$', pos);
    var inlineMathStart = replacedContent.indexOf('\\(', pos);
    if (blockMathStart === -1 && inlineMathStart === -1) {
      break;
    }
    var mathStart = void 0;
    var mathEnd = void 0;
    if (blockMathStart !== -1 && (inlineMathStart === -1 || blockMathStart < inlineMathStart)) {
      mathStart = blockMathStart;
      mathEnd = replacedContent.indexOf('$$', mathStart + 2);
    } else {
      mathStart = inlineMathStart;
      mathEnd = replacedContent.indexOf('\\)', mathStart + 2);
    }
    if (mathEnd === -1) {
      break;
    } else {
      mathEnd = mathEnd + 2;
    }
    var id = mathBlocks.length + 1;
    var placeholder = mathPlaceholder(id);
    mathBlocks.push({
      id: id,
      expression: replacedContent.slice(mathStart + 2, mathEnd - 2),
      inline: inlineMathStart !== -1
    });
    var replacement = void 0;
    if (inlineMathStart !== -1) {
      replacement = placeholder;
    } else {
      // Add new lines before and after math blocks so that they render
      // as separate paragraphs
      replacement = '\n\n' + placeholder + '\n\n';
    }
    replacedContent = replacedContent.slice(0, mathStart) + replacement + replacedContent.slice(mathEnd);
    pos = mathStart + replacement.length;
  }
  return {
    mathBlocks: mathBlocks,
    content: replacedContent
  };
}
function insertMath(html, mathBlocks) {
  return mathBlocks.reduce(function (html, block) {
    var renderedMath;
    try {
      if (block.inline) {
        renderedMath = katex.renderToString(block.expression);
      } else {
        renderedMath = katex.renderToString(block.expression, {
          displayMode: true
        });
      }
    } catch (_unused) {
      renderedMath = escapeHTML(block.expression);
    }
    return html.replace(mathPlaceholder(block.id), renderedMath);
  }, html);
}

/**
 * Convert a string of markdown and math into sanitized HTML.
 *
 * Math expressions in the input are written as LaTeX and must be enclosed in
 * `$$ .. $$` or `\( .. \)` delimiters to indicate block or inline math
 * expressions. These expressions are extracted and rendered using KaTeX.
 *
 * @return - A string of sanitized HTML.
 */
export function renderMathAndMarkdown(markdown) {
  // KaTeX takes care of escaping its input, so we want to avoid passing its
  // output through the HTML sanitizer. Therefore we first extract the math
  // blocks from the input, render and sanitize the remaining markdown and then
  // render and re-insert the math blocks back into the output.
  var mathInfo = extractMath(markdown);
  var markdownHTML = DOMPurify.sanitize(renderMarkdown(mathInfo.content));
  var mathAndMarkdownHTML = insertMath(markdownHTML, mathInfo.mathBlocks);
  return mathAndMarkdownHTML;
}
//# sourceMappingURL=render-markdown.js.map